import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Users, Star, Trophy, Gift, Crown, Receipt } from 'lucide-react';
import { VIBE_TYPE_AVATARS } from '../data/vibeAvatars';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const VIBE_TYPES = [
  { key: 'social_anchor',      emoji: '🏠', label: 'Social Anchor',      color: '#F59E0B' },
  { key: 'quiet_professional', emoji: '📚', label: 'Quiet Professional', color: '#6366F1' },
  { key: 'night_owl',          emoji: '🌙', label: 'Night Owl',          color: '#94A3B8' },
  { key: 'early_riser',        emoji: '☀️', label: 'Early Riser',        color: '#F97316' },
  { key: 'free_spirit',        emoji: '🌿', label: 'Free Spirit',        color: '#10B981' },
  { key: 'homebody',           emoji: '🛋️', label: 'Homebody',           color: '#8B5CF6' },
];

const VIBE_DIFFERENTIATORS = [
  { icon: <Zap className="w-5 h-5" />,   color: '#2cbdc4', title: 'Beyond budget & suburb',  desc: 'The only platform that matches on actual living compatibility — sleep schedules, noise levels, cleanliness, guests.' },
  { icon: <Star className="w-5 h-5" />,  color: '#F59E0B', title: 'Your Vibe Type',           desc: 'A personality type for living together. 6 types. 10-question quiz. Instantly shareable on Instagram Stories.' },
  { icon: <Users className="w-5 h-5" />, color: '#8B5CF6', title: 'Form a group first',       desc: 'Match with 1–2 compatible people, then search for a property together. No other platform does this.' },
];

const REWARDS_EARN = [
  { icon: <Receipt className="w-5 h-5" />, color: '#2cbdc4', title: 'Rent earns real points',    desc: 'Every verified rent payment earns you points equal to the AUD amount. $400 rent = 400 points, automatically.' },
  { icon: <Trophy className="w-5 h-5" />,  color: '#F59E0B', title: 'Start with a signup bonus', desc: 'Students get 500 pts, Professionals 1,000 pts, and Landlords 2,500 pts just for joining.' },
  { icon: <Gift className="w-5 h-5" />,    color: '#10B981', title: 'Redeem for real value',     desc: '10,000 pts = $10 Prezzee voucher · 10,000 pts = free month · 20,000 pts = VibeMate Plus.' },
];

const REWARDS_CATEGORIES = [
  { emoji: '🏠', label: '= $ rent',     color: '#2cbdc4', sub: 'Per payment' },
  { emoji: '👋', label: '500–2,500',    color: '#F59E0B', sub: 'Signup' },
  { emoji: '🤝', label: '1k–5k pts',   color: '#10B981', sub: 'Referrals' },
  { emoji: '📅', label: '250–1k pts',  color: '#6366F1', sub: 'Renewals' },
  { emoji: '⭐', label: '500 pts',      color: '#F97316', sub: 'Reviews' },
  { emoji: '🏢', label: '2,500 pts',   color: '#8B5CF6', sub: '1st listing' },
];

const TABS = ['vibemate', 'rewards'];

// ── Rewards Journey Timeline Card ────────────────────────────────────────────
const MILESTONES = [
  { pts: 500,    icon: '🎉', label: 'Welcome',          reward: 'Signup bonus collected',     color: '#2cbdc4' },
  { pts: 10000,  icon: '🎁', label: '$10 Gift Voucher',  reward: 'Prezzee gift card',           color: '#10B981' },
  { pts: 10000,  icon: '👑', label: '1 Month Free',      reward: 'Subscription credit',         color: '#6366F1' },
  { pts: 20000,  icon: '⚡', label: 'VibeMate Plus',     reward: '1 month upgrade',             color: '#8B5CF6' },
  { pts: 50000,  icon: '🚀', label: '$50 Voucher',       reward: 'Prezzee big reward',          color: '#F59E0B' },
  { pts: 100000, icon: '🏆', label: 'Elite Member',      reward: 'Max rewards achieved!',       color: '#F97316' },
];

const EARN_ACTIONS = [
  { label: 'Refer a landlord',       pts: 5000, icon: '🏢', time: '1 referral' },
  { label: 'Refer a professional',   pts: 2500, icon: '💼', time: '1 referral' },
  { label: 'Refer a student',        pts: 1000, icon: '🎓', time: '1 referral' },
  { label: 'Submit property review', pts: 500,  icon: '⭐', time: '5 minutes' },
  { label: 'Complete VibeMate quiz', pts: 375,  icon: '💫', time: '5 minutes' },
  { label: 'Verify rent receipt',    pts: 350,  icon: '🏠', time: 'Per month' },
  { label: 'Monthly renewal',        pts: 250,  icon: '📅', time: 'Auto-earned' },
];

function RewardsJourneyCard() {
  const DEMO_PTS = 1250;
  const [goalIdx, setGoalIdx] = useState(null); // index into MILESTONES

  const goal = goalIdx !== null ? MILESTONES[goalIdx] : null;
  const ptsNeeded = goal ? Math.max(0, goal.pts - DEMO_PTS) : 0;

  // Fastest earn actions toward goal, sorted by pts descending
  const earnToward = goal
    ? [...EARN_ACTIONS]
        .filter(a => a.pts <= ptsNeeded || ptsNeeded === 0)
        .sort((a, b) => b.pts - a.pts)
        .slice(0, 3)
    : [];

  // If selected goal is already achieved
  const goalAchieved = goal && DEMO_PTS >= goal.pts;

  // Position of current pts as % between milestones (for the line fill)
  const maxPts = MILESTONES[MILESTONES.length - 1].pts;
  const progressPct = Math.min(100, (DEMO_PTS / maxPts) * 100);

  return (
    <div className="relative w-[285px] rounded-3xl border border-white/15 backdrop-blur-sm overflow-hidden"
      style={{ background: 'rgba(8,30,45,0.88)' }}>

      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <span className="text-[10px] text-white/40 uppercase tracking-widest font-bold">Rewards Journey</span>
        <div className="flex items-center gap-1.5 bg-white/8 rounded-full px-2.5 py-1">
          <Trophy className="w-3 h-3 text-[#F59E0B]" />
          <span className="text-xs font-black text-white">{DEMO_PTS.toLocaleString()}</span>
          <span className="text-xs text-white/40">pts</span>
        </div>
      </div>

      {/* Instruction */}
      <p className="text-[10px] text-white/30 px-5 pb-3 leading-tight">
        {goalIdx === null ? 'Tap a milestone to set your goal →' : `Goal set! Here's how to reach it:`}
      </p>

      {/* Scrollable timeline */}
      <div className="px-4 pb-2" style={{ overflowY: 'auto', maxHeight: '240px', scrollbarWidth: 'none' }}>
        <div className="relative pl-7">
          {/* Vertical track line */}
          <div className="absolute left-3 top-3 bottom-3 w-px bg-white/10" />
          {/* Progress fill */}
          <div className="absolute left-3 top-3 w-px bg-[#2cbdc4] transition-all duration-500"
            style={{ height: `${progressPct}%` }} />

          {MILESTONES.map((m, i) => {
            const achieved = DEMO_PTS >= m.pts;
            const isNext = !achieved && (i === 0 || DEMO_PTS >= MILESTONES[i - 1].pts);
            const isGoal = goalIdx === i;
            const isFuture = !achieved && !isNext;

            return (
              <div key={i} className="relative mb-4 last:mb-0">
                {/* Node */}
                <div className="absolute -left-7 top-0.5 flex items-center justify-center"
                  style={{ width: '22px', height: '22px' }}>
                  {achieved ? (
                    <div className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ background: '#2cbdc4', border: '2px solid rgba(44,189,196,0.4)' }}>
                      <span style={{ fontSize: '9px' }}>✓</span>
                    </div>
                  ) : isGoal ? (
                    <div className="w-5 h-5 rounded-full animate-pulse"
                      style={{ background: m.color, border: `2px solid ${m.color}`, boxShadow: `0 0 8px ${m.color}60` }} />
                  ) : isNext ? (
                    <div className="w-5 h-5 rounded-full border-2 border-dashed border-[#2cbdc4]/60"
                      style={{ background: 'rgba(44,189,196,0.1)' }} />
                  ) : (
                    <div className="w-4 h-4 rounded-full border border-white/20"
                      style={{ background: 'rgba(255,255,255,0.04)' }} />
                  )}
                </div>

                {/* Row content */}
                <button
                  onClick={() => !achieved && setGoalIdx(goalIdx === i ? null : i)}
                  className={`w-full text-left rounded-2xl px-3 py-2 transition-all duration-200 ${
                    isGoal
                      ? 'border'
                      : achieved
                      ? 'opacity-60'
                      : 'hover:bg-white/5 border border-transparent hover:border-white/8'
                  }`}
                  style={isGoal ? { background: `${m.color}12`, borderColor: `${m.color}40` } : {}}
                >
                  <div className="flex items-center gap-2">
                    <span style={{ fontSize: '14px', lineHeight: 1 }}>{m.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-bold ${achieved ? 'text-white/50 line-through' : isGoal ? 'text-white' : 'text-white/70'}`}>
                          {m.label}
                        </span>
                        <span className="text-[10px] font-black ml-2 flex-shrink-0"
                          style={{ color: achieved ? '#2cbdc4' : isGoal ? m.color : 'rgba(255,255,255,0.3)' }}>
                          {m.pts.toLocaleString()} pts
                        </span>
                      </div>
                      <p className="text-[9px] text-white/30 leading-tight mt-0.5 truncate">{m.reward}</p>
                    </div>
                    {isGoal && <span className="text-[9px] font-bold text-amber-400 flex-shrink-0 ml-1">GOAL</span>}
                  </div>
                </button>
              </div>
            );
          })}

          {/* "You are here" indicator between milestones */}
          {!MILESTONES.some(m => DEMO_PTS === m.pts) && (
            <div className="absolute -left-[31px] flex items-center gap-1.5"
              style={{ top: `${progressPct * 0.88}%` }}>
              <div className="w-3 h-3 rounded-full bg-[#F59E0B] animate-pulse"
                style={{ boxShadow: '0 0 6px rgba(245,158,11,0.6)' }} />
              <span className="text-[8px] text-[#F59E0B] font-bold whitespace-nowrap bg-[#0d2535] px-1 rounded">
                You · {DEMO_PTS.toLocaleString()} pts
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Goal earn section */}
      {goal && (
        <div className="mx-4 mb-4 mt-2 rounded-2xl p-3 border"
          style={{ background: 'rgba(0,0,0,0.25)', borderColor: `${goal.color}30` }}>
          {goalAchieved ? (
            <p className="text-xs text-[#2cbdc4] font-bold text-center">
              ✓ You&apos;ve already reached this milestone!
            </p>
          ) : (
            <>
              <p className="text-[10px] text-white/40 uppercase tracking-wide font-bold mb-2">
                Need {ptsNeeded.toLocaleString()} more pts · fastest ways:
              </p>
              <div className="space-y-2">
                {(earnToward.length > 0 ? earnToward : EARN_ACTIONS.sort((a, b) => b.pts - a.pts).slice(0, 3)).map((a, i) => {
                  const covers = Math.round((a.pts / ptsNeeded) * 100);
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <span style={{ fontSize: '12px' }}>{a.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-white/60 truncate">{a.label}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <div className="flex-1 bg-white/8 rounded-full h-1">
                            <div className="h-1 rounded-full transition-all"
                              style={{ width: `${Math.min(100, covers)}%`, background: goal.color }} />
                          </div>
                          <span className="text-[9px] font-black flex-shrink-0" style={{ color: goal.color }}>
                            +{a.pts.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Horizontal Rewards Timeline (frameless, left → right) ────────────────────
const HZ_MILESTONES = [
  { pts: 500,    bonus: 0,    icon: '🎉', label: 'Welcome',        color: '#2cbdc4', short: 'Signup bonus'    },
  { pts: 10000,  bonus: 500,  icon: '🎁', label: '$10 Voucher',    color: '#10B981', short: 'Prezzee card'    },
  { pts: 10000,  bonus: 500,  icon: '👑', label: '1 Month Free',   color: '#6366F1', short: 'Subscription'    },
  { pts: 20000,  bonus: 1000, icon: '⚡', label: 'VibeMate+',      color: '#8B5CF6', short: '1 mo upgrade'    },
  { pts: 50000,  bonus: 2500, icon: '🚀', label: '$50 Voucher',    color: '#F59E0B', short: 'Prezzee big'     },
  { pts: 100000, bonus: 5000, icon: '🏆', label: 'Elite',          color: '#F97316', short: '+5k anniversary' },
];
const HZ_POSITIONS = [8, 24, 42, 60, 78, 92]; // evenly inset — avoids edge clipping

const HZ_EARN = [
  { label: 'Refer a landlord',       pts: 5000, icon: '🏢' },
  { label: 'Refer a professional',   pts: 2500, icon: '💼' },
  { label: 'Refer a student',        pts: 1000, icon: '🎓' },
  { label: 'Submit property review', pts: 500,  icon: '⭐' },
  { label: 'VibeMate quiz',          pts: 375,  icon: '💫' },
  { label: 'Verify rent receipt',    pts: 350,  icon: '🏠' },
];

function HorizontalRewardsTimeline() {
  const { user, token } = useAuth();
  const [state, dispatch] = React.useReducer(
    (s, a) => ({ ...s, ...a }),
    { balance: null, goalIdx: null }
  );
  const balance  = state.balance;
  const goalIdx  = state.goalIdx;
  const setGoalIdx = (v) => dispatch({ goalIdx: v });

  const loadBalance = React.useCallback(() => {
    if (!token) { dispatch({ balance: null }); return; }
    fetch(`${API}/api/rewards/balance`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (typeof d.balance === 'number') dispatch({ balance: d.balance }); })
      .catch(() => dispatch({ balance: 0 }));
  }, [token]);

  useEffect(() => { loadBalance(); }, [loadBalance]);

  // Use real balance for logged-in users; demo otherwise
  const DEMO_PTS = 1250;
  const currentPts = user && balance !== null ? balance : DEMO_PTS;
  const isDemo = !user || balance === null;

  const getYouHereX = () => {
    for (let i = 0; i < HZ_MILESTONES.length - 1; i++) {
      if (currentPts >= HZ_MILESTONES[i].pts && currentPts < HZ_MILESTONES[i + 1].pts) {
        const seg = (currentPts - HZ_MILESTONES[i].pts) / (HZ_MILESTONES[i + 1].pts - HZ_MILESTONES[i].pts);
        return HZ_POSITIONS[i] + seg * (HZ_POSITIONS[i + 1] - HZ_POSITIONS[i]);
      }
    }
    return currentPts >= HZ_MILESTONES[HZ_MILESTONES.length - 1].pts ? 100 : 0;
  };

  const youX = getYouHereX();
  const goal = goalIdx !== null ? HZ_MILESTONES[goalIdx] : null;
  const ptsNeeded = goal ? Math.max(0, goal.pts - currentPts) : 0;
  const earnToward = goal ? [...HZ_EARN].sort((a, b) => b.pts - a.pts).slice(0, 4) : [];

  return (
    <div className="mb-10">
      {/* Scrollable on mobile */}
      <div style={{ overflowX: 'auto', paddingBottom: '4px' }}>
        <div style={{ minWidth: '680px', position: 'relative', padding: '0 12px' }}>

          {/* TOP ROW: icons + labels (odd indices) */}
          <div style={{ display: 'flex', position: 'relative', height: '52px', marginBottom: '6px' }}>
            {HZ_MILESTONES.map((m, i) => (
              <div key={i} style={{
                position: 'absolute',
                left: `${HZ_POSITIONS[i]}%`,
                transform: 'translateX(-50%)',
                textAlign: 'center',
                bottom: 0,
                display: i % 2 === 0 ? 'block' : 'none',
                width: '90px',
                marginLeft: '-45px',
              }}>
                <span style={{ fontSize: '20px', display: 'block', lineHeight: 1 }}>{m.icon}</span>
                <span style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.75)', display: 'block', whiteSpace: 'nowrap', marginTop: '3px' }}>{m.label}</span>
                <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)', display: 'block' }}>{m.short}</span>
              </div>
            ))}
          </div>

          {/* TRACK LINE + NODES */}
          <div style={{ position: 'relative', height: '24px' }}>
            {/* Track background */}
            <div style={{
              position: 'absolute', top: '50%', left: 0, right: 0, height: '3px',
              transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.10)', borderRadius: '2px'
            }} />
            {/* Progress fill to current pos */}
            <div style={{
              position: 'absolute', top: '50%', left: 0, height: '3px',
              width: `${youX}%`,
              transform: 'translateY(-50%)',
              background: 'linear-gradient(90deg,#2cbdc4,#094f6f)', borderRadius: '2px',
              transition: 'width 0.5s ease'
            }} />
            {/* Goal fill (current to goal) */}
            {goal && goalIdx !== null && !HZ_MILESTONES[goalIdx] || false ? null : goal ? (
              <div style={{
                position: 'absolute', top: '50%', left: `${youX}%`, height: '3px',
                width: `${HZ_POSITIONS[goalIdx] - youX}%`,
                transform: 'translateY(-50%)',
                background: `linear-gradient(90deg,${goal.color}60,${goal.color}20)`,
                borderRadius: '2px',
              }} />
            ) : null}

            {/* Milestone nodes */}
            {HZ_MILESTONES.map((m, i) => {
              const achieved = currentPts >= m.pts;
              const isGoal = goalIdx === i;
              return (
                <button key={i} onClick={() => !achieved && setGoalIdx(goalIdx === i ? null : i)}
                  style={{
                    position: 'absolute',
                    left: `${HZ_POSITIONS[i]}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: isGoal ? '22px' : '18px',
                    height: isGoal ? '22px' : '18px',
                    borderRadius: '50%',
                    border: `2.5px solid ${achieved ? '#2cbdc4' : isGoal ? m.color : 'rgba(255,255,255,0.25)'}`,
                    background: achieved ? '#2cbdc4' : isGoal ? `${m.color}35` : 'rgba(8,30,45,0.9)',
                    cursor: achieved ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: isGoal ? `0 0 12px ${m.color}80, 0 0 4px ${m.color}` : 'none',
                    zIndex: 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  {achieved && <span style={{ fontSize: '8px', color: 'white', fontWeight: 900 }}>✓</span>}
                  {isGoal && !achieved && <span style={{ fontSize: '8px', color: m.color, fontWeight: 900 }}>★</span>}
                </button>
              );
            })}

            {/* "You are here" pulsing dot */}
            <div style={{
              position: 'absolute', left: `${youX}%`, top: '50%',
              transform: 'translate(-50%, -50%)', zIndex: 3,
              width: '11px', height: '11px', borderRadius: '50%',
              background: '#F59E0B',
              boxShadow: '0 0 10px rgba(245,158,11,0.8), 0 0 4px rgba(245,158,11,0.5)',
              animation: 'vibeMarqueePulse 1.8s ease-in-out infinite',
            }} />

            {/* "You" label above dot */}
            <div style={{
              position: 'absolute', left: `${youX}%`, top: '-22px',
              transform: 'translateX(-50%)',
              background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)',
              borderRadius: '20px', padding: '1px 7px', whiteSpace: 'nowrap',
            }}>
              <span style={{ fontSize: '9px', fontWeight: 800, color: '#F59E0B' }}>
                {user ? `You · ${currentPts.toLocaleString()} pts` : `Demo · ${currentPts.toLocaleString()} pts`}
              </span>
            </div>
          </div>

          {/* BOTTOM ROW: pts value + milestone bonus */}
          <div style={{ position: 'relative', height: '48px', marginTop: '8px' }}>
            {HZ_MILESTONES.map((m, i) => (
              <div key={i} style={{
                position: 'absolute',
                left: `${HZ_POSITIONS[i]}%`,
                transform: 'translateX(-50%)',
                textAlign: 'center',
                top: 0,
                width: '90px', marginLeft: '-45px',
              }}>
                {/* Odd milestones: show icon + name below (alternating) */}
                {i % 2 === 1 && (
                  <>
                    <span style={{ fontSize: '20px', display: 'block', lineHeight: 1 }}>{m.icon}</span>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.75)', display: 'block', whiteSpace: 'nowrap', marginTop: '1px' }}>{m.label}</span>
                  </>
                )}
                <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', display: 'block', whiteSpace: 'nowrap', marginTop: i % 2 === 1 ? '1px' : '2px' }}>
                  {m.pts.toLocaleString()} pts
                </span>
                {m.bonus > 0 && (
                  <span style={{ fontSize: '9px', fontWeight: 800, color: m.color, display: 'block', whiteSpace: 'nowrap' }}>
                    +{m.bonus.toLocaleString()} bonus
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Goal earn panel */}
      {goal && (
        <div className="mt-6 rounded-2xl p-4"
          style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${goal.color}30` }}>
          <div className="flex items-center gap-2 mb-3">
            <span style={{ fontSize: '16px' }}>{goal.icon}</span>
            <p className="text-sm font-bold text-white">Goal: {goal.label}</p>
            <span className="ml-auto text-xs font-black" style={{ color: goal.color }}>
              {ptsNeeded > 0 ? `${ptsNeeded.toLocaleString()} pts to go` : 'Already reached!'}
            </span>
          </div>
          {goal.bonus > 0 && (
            <p className="text-xs text-white/40 mb-3">
              Reach this milestone and earn an extra <strong style={{ color: goal.color }}>+{goal.bonus.toLocaleString()} bonus pts</strong> automatically
            </p>
          )}
          {ptsNeeded > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {earnToward.map((a, i) => (
                <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <span style={{ fontSize: '14px' }}>{a.icon}</span>
                  <p className="text-xs text-white/60 mt-1 leading-tight">{a.label}</p>
                  <p className="text-xs font-black mt-1" style={{ color: goal.color }}>+{a.pts.toLocaleString()} pts</p>
                  <div className="mt-1.5 bg-white/8 rounded-full h-1">
                    <div className="h-1 rounded-full" style={{ width: `${Math.min(100, (a.pts / ptsNeeded) * 100)}%`, background: goal.color }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Login nudge for logged-out visitors */}
      {isDemo && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <div className="h-px flex-1 bg-white/10" />
          <p className="text-xs text-white/30 whitespace-nowrap">
            Demo preview —{' '}
            <Link to="/login" className="text-[#2cbdc4] hover:text-white font-semibold transition-colors">
              Log in to see your real progress
            </Link>
          </p>
          <div className="h-px flex-1 bg-white/10" />
        </div>
      )}

      {/* Hint when no goal selected */}
      {!goal && (
        <p className="text-center text-xs text-white/25 mt-4">
          Tap a milestone node to set your goal and see how to get there
        </p>
      )}
    </div>
  );
}

// ── Infinite-scroll marquee pill card ────────────────────────────────────────
function VibePill({ vibe }) {
  const avatarUrl = VIBE_TYPE_AVATARS[vibe.key];
  return (
    <div
      style={{
        flexShrink: 0,
        width: '130px',
        height: '260px',
        borderRadius: '70px',
        background: 'rgba(255,255,255,0.06)',
        border: '1.5px solid rgba(255,255,255,0.12)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* Avatar fills top ~68% */}
      <div style={{ flex: '0 0 68%', overflow: 'hidden', borderRadius: '68px 68px 0 0' }}>
        {avatarUrl
          ? <img src={avatarUrl} alt={vibe.label} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} loading="lazy" />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', background: `${vibe.color}20` }}>{vibe.emoji}</div>
        }
      </div>
      {/* Label at bottom */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 10px' }}>
        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', textAlign: 'center', lineHeight: '1.3', fontWeight: 600 }}>
          {vibe.label}
        </span>
      </div>
    </div>
  );
}

export default function VibemateUSP() {
  const [activeTab, setActiveTab] = useState(0);
  const [activeType, setActiveType] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  // Cycle vibe types for the card
  useEffect(() => {
    const t = setInterval(() => setActiveType(i => (i + 1) % VIBE_TYPES.length), 2800);
    return () => clearInterval(t);
  }, []);

  // Auto-rotate tabs every 6 seconds
  useEffect(() => {
    const t = setInterval(() => {
      setTransitioning(true);
      setTimeout(() => { setActiveTab(i => (i + 1) % 2); setTransitioning(false); }, 300);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  const switchTab = (idx) => {
    if (idx === activeTab) return;
    setTransitioning(true);
    setTimeout(() => { setActiveTab(idx); setTransitioning(false); }, 250);
  };

  const isVibemate = activeTab === 0;
  const vt = VIBE_TYPES[activeType];
  const vtAvatarUrl = VIBE_TYPE_AVATARS[vt.key];

  return (
    <section className="py-20 overflow-hidden" style={{ background: 'linear-gradient(180deg, #04303f 0%, #062736 60%, #071e2a 100%)' }}>

      {/* Inline keyframe for marquee */}
      <style>{`
        @keyframes vibeMarquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .vibe-marquee-track {
          animation: vibeMarquee 28s linear infinite;
          display: flex;
          gap: 14px;
          width: max-content;
          will-change: transform;
        }
        .vibe-marquee-track:hover {
          animation-play-state: paused;
        }
        @keyframes vibeMarqueePulse {
          0%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.7; transform: translate(-50%, -50%) scale(1.35); }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Tab switcher */}
        <div className="flex justify-center mb-10">
          <div className="flex bg-white/8 rounded-full p-1 border border-white/10">
            {[
              { id: 'vibemate', icon: <Users className="w-3.5 h-3.5" />, label: 'VibeMate™' },
              { id: 'rewards',  icon: <Trophy className="w-3.5 h-3.5" />, label: 'Rewards' },
            ].map((tab, i) => (
              <button key={tab.id} onClick={() => switchTab(i)}
                data-testid={`usp-tab-${tab.id}`}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${activeTab === i ? 'text-white shadow-lg' : 'text-white/40 hover:text-white/70'}`}
                style={activeTab === i ? { background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' } : {}}>
                {tab.icon}{tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Animated content */}
        <div className={`transition-all duration-300 ${transitioning ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>

          {/* Badge + headline */}
          <div className="flex justify-center mb-6">
            <span className="inline-flex items-center gap-2 bg-white/10 text-white/70 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full border border-white/10">
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: isVibemate ? '#2cbdc4' : '#F59E0B' }} />
              {isVibemate ? 'Only on RentalX' : 'New: RentalX Rewards'}
            </span>
          </div>

          <div className="text-center mb-14">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight mb-4">
              {isVibemate
                ? <>Meet <span style={{ color: '#2cbdc4' }}>VibeMate™</span></>
                : <>Rent smarter, <span style={{ color: '#F59E0B' }}>earn rewards</span></>}
            </h2>
            <p className="text-xl text-white/50 max-w-2xl mx-auto leading-relaxed">
              {isVibemate
                ? "The world's first housemate compatibility engine — built into your property search. Not just who's renting near you. Who you'll actually enjoy living with."
                : 'Every rent payment, every referral, every action earns real points redeemable for gift vouchers, free months, and more. 1,000 pts = A$1.'}
            </p>
          </div>

          {/* ── Layouts split by tab ── */}
          {isVibemate ? (
            /* ── VibeMate: 2-col grid + marquee ── */
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
                <div className="space-y-5">
                  {VIBE_DIFFERENTIATORS.map((d, i) => (
                    <div key={i} className="flex gap-4 p-5 rounded-2xl border border-white/8 bg-white/4 hover:bg-white/8 transition-colors">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${d.color}20`, color: d.color }}>{d.icon}</div>
                      <div>
                        <h3 className="font-bold text-white mb-1">{d.title}</h3>
                        <p className="text-sm text-white/50 leading-relaxed">{d.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-center lg:justify-end">
                  <div className="relative">
                    <div className="absolute inset-0 rounded-3xl blur-2xl opacity-25 transition-all duration-700"
                      style={{ background: `radial-gradient(circle,${vt.color},transparent)` }} />
                    <div className="relative w-[280px] rounded-3xl border border-white/15 p-6 backdrop-blur-sm"
                      style={{ background: 'rgba(8,30,45,0.85)' }}>
                      <div className="flex items-center justify-between mb-5">
                        <span className="text-[10px] text-white/40 uppercase tracking-widest font-bold">Your Vibe Type</span>
                        <div className="flex gap-1.5">
                          {VIBE_TYPES.map((_, i) => (
                            <div key={i} className="rounded-full transition-all duration-300"
                              style={{ width: i === activeType ? '16px' : '5px', height: '5px', background: i === activeType ? '#2cbdc4' : 'rgba(255,255,255,0.2)' }} />
                          ))}
                        </div>
                      </div>
                      <div className="mx-auto mb-4 rounded-2xl overflow-hidden"
                        style={{ width: '160px', height: '160px', background: `${vt.color}15`, border: `2px solid ${vt.color}30` }}>
                        {vtAvatarUrl
                          ? <img src={vtAvatarUrl} alt={vt.label} className="w-full h-full object-cover transition-all duration-500" />
                          : <div className="w-full h-full flex items-center justify-center text-6xl">{vt.emoji}</div>}
                      </div>
                      <div className="text-center mb-5">
                        <h3 className="text-xl font-black text-white mb-1">The {vt.label}</h3>
                        <p className="text-xs text-white/35">Calculated from your quiz answers</p>
                      </div>
                      <div className="rounded-2xl p-4 space-y-3" style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <p className="text-[10px] text-white/40 uppercase tracking-widest font-semibold">Match Compatibility</p>
                        {[['Living rhythm', 88], ['Vibe type', 92], ['Lifestyle values', 79]].map(([label, score]) => (
                          <div key={label} className="flex items-center gap-3">
                            <span className="text-xs text-white/50 w-28">{label}</span>
                            <div className="flex-1 bg-white/8 rounded-full h-1.5">
                              <div className="h-1.5 rounded-full" style={{ width: `${score}%`, background: 'linear-gradient(90deg,#2cbdc4,#0d6e8a)' }} />
                            </div>
                            <span className="text-xs text-white/60 font-bold w-8 text-right">{score}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Vibe marquee */}
              <div className="mb-12" style={{ overflow: 'hidden' }}>
                <div className="vibe-marquee-track">
                  {[...VIBE_TYPES, ...VIBE_TYPES].map((v, i) => <VibePill key={i} vibe={v} />)}
                </div>
              </div>
            </>
          ) : (
            /* ── Rewards: 3 compact feature cards + horizontal frameless timeline ── */
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                {REWARDS_EARN.map((d, i) => (
                  <div key={i} className="flex gap-3 p-4 rounded-2xl border border-white/8 bg-white/4 hover:bg-white/8 transition-colors items-start">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${d.color}20`, color: d.color }}>
                      {d.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm mb-0.5">{d.title}</h3>
                      <p className="text-xs text-white/45 leading-relaxed">{d.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <HorizontalRewardsTimeline />
            </>
          )}

          {/* CTA */}
          <div className="text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4">
              {isVibemate ? (
                <>
                  <Link to="/vibemate"
                    className="flex items-center gap-2 px-8 py-4 rounded-2xl text-base font-bold text-white hover:opacity-90 transition-all hover:scale-105 shadow-lg"
                    style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
                    Discover VibeMate <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/vibemate" className="text-sm text-white/40 hover:text-white/70 transition-colors">
                    Take the quiz — find your Vibe Type →
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/how-rewards-work"
                    className="flex items-center gap-2 px-8 py-4 rounded-2xl text-base font-bold text-white hover:opacity-90 transition-all hover:scale-105 shadow-lg"
                    style={{ background: 'linear-gradient(135deg,#F59E0B,#F97316)' }}>
                    How Rewards Work <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/student-dashboard" className="text-sm text-white/40 hover:text-white/70 transition-colors">
                    View my wallet →
                  </Link>
                </>
              )}
            </div>
            <p className="text-xs text-white/25 mt-4">
              {isVibemate ? 'Exclusive to RentalX · Free for all users' : '1,000 pts = A$1 · Up to 50,000 pts per account'}
            </p>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-10">
            {TABS.map((_, i) => (
              <button key={i} onClick={() => switchTab(i)}
                className="rounded-full transition-all duration-300"
                style={{
                  width: activeTab === i ? '1.5rem' : '0.4rem',
                  height: '0.4rem',
                  background: activeTab === i ? (i === 0 ? '#2cbdc4' : '#F59E0B') : 'rgba(255,255,255,0.2)',
                }} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
